import React, { useEffect, useState } from 'react'

import ShareIcon from '@mui/icons-material/Share'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { useSelector } from 'react-redux'

import type { RootState } from 'reduxStore'

import PoziAPIData from 'api/data'
import { GetHistoryURL } from 'api/history'
import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import AccordionWrapper from 'drawer/tabs/AccordionWrapper'
import PoziNotifications from 'utils/notifications'

import { POZI_SHARE_LINK_URL, POZI_SHARE_REDIRECTOR_URL } from '../share'

const styles = {
  root: {
    padding: '10px 0px 15px 26px'
  }
}

/**
 * Generates a unique ID for the share link that is a random string of six alphanumeric characters.
 * @returns A unique ID for the share link.
 */
const generateUID = (): string => {
  // Generate the UID from two parts here to ensure the random number provide enough bits.
  const firstPartNumber = (Math.random() * 46656) | 0
  const secondPartNumber = (Math.random() * 46656) | 0
  const firstPart = ('000' + firstPartNumber.toString(36)).slice(-3)
  const secondPart = ('000' + secondPartNumber.toString(36)).slice(-3)
  return firstPart + secondPart + ''
}

/**
 * Copies text to the end user's clipboard.
 * @param text The text to copy to the clipboard.
 * @returns True if the text was copied to the clipboard, false otherwise.
 */
const copyToClipboard = async (text: string): Promise<boolean> => {
  // Try modern Clipboard API first
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text)
      // console.debug('ToolsPanelShare - Clipboard API succeeded')
      return true
    } catch (err) {
      console.debug('ToolsPanelShare - Clipboard API failed, trying fallback:', err)
    }
  }

  // Fallback to execCommand with temporary textarea
  try {
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.style.position = 'fixed'
    textArea.style.left = '-9999px'
    textArea.style.top = '0'
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()
    const success = document.execCommand('copy')
    document.body.removeChild(textArea)
    return success
  } catch (err) {
    console.error('ToolsPanelShare - execCommand fallback failed:', err)
    return false
  }
}

interface ToolsPanelShareProps {
  toggleExpansionPanel: () => void
}

const ToolsPanelShare: React.FC<ToolsPanelShareProps> = ({ toggleExpansionPanel }) => {
  const tools = useSelector((state: RootState) => state?.config?.layout?.drawer?.tabs?.tools)
  const xCoordinate = useSelector((state: RootState) => state?.mapX)
  const yCoordinate = useSelector((state: RootState) => state?.mapY)
  const zoomLevel = useSelector((state: RootState) => state?.mapZ)
  const [isCreatingLink, setIsCreatingLink] = useState(false)
  const [isNotificationVisible, setIsNotificationVisible] = useState(false)

  const title = tools?.share?.title ?? 'Share'
  const expanded = tools?.share?.expanded ?? true

  useEffect(() => {
    if (!isNotificationVisible) {
      return
    }
    PoziNotifications().hideAll()
    setIsNotificationVisible(false)
  }, [xCoordinate, yCoordinate, zoomLevel])

  const handleShareClick = async () => {
    // console.debug('ToolsPanelShare - shareURLButton clicked')
    PoziUtils.gtag({
      category: 'useraction',
      status: 'success',
      identifier: 'Share button',
      action: 'Click share button'
    })

    const uid = generateUID()
    Model.set('app.drawer.tabs.tools.share.uid', uid)
    const shortURL = `${POZI_SHARE_LINK_URL}/#/${uid}`

    // Get the current URL
    let currentUrl = GetHistoryURL(true)
    // Remove any tab parameter from the URL. If a feature is selected, it will automatically open the "Info" tab.
    currentUrl = currentUrl.replace(/\/tab\[[^\]]+\]/g, '')

    const currentSite = Model.get('app.site')

    const shareLinkData = {
      site: currentSite,
      url: currentUrl,
      view: uid
    }

    setIsCreatingLink(true)

    // Copy to clipboard immediately
    const isLinkCopied = await copyToClipboard(shortURL)

    // Use the redirector API to create the share link
    PoziAPIData().ajax({
      url: POZI_SHARE_REDIRECTOR_URL,
      method: 'POST',
      data: shareLinkData,
      error: (jqXHR: any, textStatus: any, errorThrown: any) => {
        console.error('Unable to save share link.', jqXHR, textStatus, errorThrown)
        setIsCreatingLink(false)
        PoziNotifications().error('Failed to save share link.')
      },
      success: (response: any, textStatus: any, jqXHR: any) => {
        // console.debug('Share link creation response.', response)
        setIsCreatingLink(false)

        if (!response?.success) {
          const msg = response?.message || ''
          PoziNotifications().error('Failed to save share link! ' + msg)
          return
        }

        // Show success notification with clickable URL
        const notificationMessage = (
          <span>
            {isLinkCopied ? 'Link copied to your clipboard: ' : 'Link created: '}
            <a href={shortURL} target="_blank" rel="noopener noreferrer">
              {shortURL}
            </a>
          </span>
        )
        PoziNotifications().notify(notificationMessage)
        setIsNotificationVisible(true)
      }
    })
  }

  return (
    <AccordionWrapper expanded={expanded} onChange={toggleExpansionPanel} headerIcon={<ShareIcon />} title={title} bExcludeExpandIcon={false}>
      <Stack direction="column" sx={styles.root}>
        <Button variant="outlined" startIcon={<ShareIcon />} onClick={handleShareClick} disabled={isCreatingLink}>
          {isCreatingLink ? 'Creating link...' : 'Share'}
        </Button>
      </Stack>
    </AccordionWrapper>
  )
}

export default ToolsPanelShare
